import * as hotpadsEvents from '@/src/analytics/hotpads/events';
import type { HotpadsClickstreamListing } from '@/src/analytics';

type AnalyticsPayload = {
  listing?: HotpadsClickstreamListing | null;
  mode?: 'contact' | 'requestToApply' | 'tour';
  phase?: 'open' | 'submit' | 'reschedule';
  rentalSubmitId?: string;
  tab?: string;
  triggerLocation?: string;
  triggerObject?: string;
  triggerSource?: string;
};

type ApiPayload = Record<string, unknown> | undefined;

function devLog(name: string, payload?: ApiPayload) {
  if (process.env.NODE_ENV === 'production') return;

  console.debug(`[halo:analytics] ${name}`, payload ?? {});
}

function isDemotedLead(listing: HotpadsClickstreamListing | null | undefined) {
  return Boolean(
    listing?.isDemoted ||
      (listing as { details?: { demoted?: boolean | null } } | null | undefined)?.details?.demoted,
  );
}

function isPaidLead(listing: HotpadsClickstreamListing | null | undefined) {
  return Boolean(listing?.isPaidAndNotDemoted || listing?.isPaidMultifamily);
}

export const emitTrackSaveHomeClickEvent = (payload: AnalyticsPayload = {}) =>
  hotpadsEvents.emitTrackSaveHomeClickEvent(payload);

export const emitTrackUnSaveHomeClickEvent = (payload: AnalyticsPayload = {}) =>
  hotpadsEvents.emitTrackUnSaveHomeClickEvent(payload);

export const emitTrackViewHdpEvent = (payload: AnalyticsPayload = {}) => hotpadsEvents.emitTrackViewHdpEvent(payload);

export const emitTrackUpperPpcClickEvent = (payload: AnalyticsPayload = {}) =>
  hotpadsEvents.emitTrackUpperPpcClickEvent(payload);

export const emitTrackLowerPpcClickEvent = (payload: AnalyticsPayload = {}) =>
  hotpadsEvents.emitTrackLowerPpcClickEvent(payload);

export const emitTrackBuildingPayPerLinkClickEvent = (payload: AnalyticsPayload = {}) =>
  hotpadsEvents.emitTrackBuildingPayPerLinkClickEvent(payload);

export const emitTrackAmenityClickEvent = (payload: AnalyticsPayload = {}) =>
  hotpadsEvents.emitTrackHdpPhotoGalleryOpenEvent(payload);

export function emitTrackContactClickEvent(payload: AnalyticsPayload = {}) {
  if (payload.phase === 'submit' && payload.rentalSubmitId) {
    const triggerSource =
      payload.triggerSource ??
      (payload.mode === 'requestToApply' ? 'button_to_complete_request_to_apply_form' : 'button_to_complete_contact_form');

    if (payload.triggerObject === 'home_details_component|inline_form') {
      hotpadsEvents.emitTrackInlineContactSubmitEvent({
        ...payload,
        rentalSubmitId: payload.rentalSubmitId,
      });
      return;
    }

    hotpadsEvents.emitTrackModalContactSubmitEvent({
      ...payload,
      rentalSubmitId: payload.rentalSubmitId,
      triggerSource,
    });
    return;
  }

  hotpadsEvents.emitTrackRequestContactStartEvent(payload);
}

export const emitTrackTourClickEvent = (payload?: AnalyticsPayload) => {
  void payload;
};

export function emitTrackApplyClickEvent(payload: AnalyticsPayload = {}) {
  hotpadsEvents.emitTrackApplyNowClickEvent(payload);
  hotpadsEvents.emitTrackRentalAppPreStartEvent(payload);
}

export const emitTrackShareClickEvent = (payload: AnalyticsPayload = {}) => hotpadsEvents.emitTrackShareHomeEvent(payload);

export function emitTrackPhoneLeadEvent(payload: AnalyticsPayload = {}) {
  if (isDemotedLead(payload.listing)) {
    hotpadsEvents.emitTrackPhoneLeadForDemotedPropertyEvent(payload);
    return;
  }

  if (isPaidLead(payload.listing)) {
    hotpadsEvents.emitTrackPhoneLeadForPaidPropertyEvent(payload);
    return;
  }

  hotpadsEvents.emitTrackPhoneLeadForFreePropertyEvent(payload);
}

export const emitTrackHideHomeFromHdpEvent = (payload: AnalyticsPayload = {}) =>
  hotpadsEvents.emitTrackHideHomeFromHdpEvent(payload);

export const emitTrackUnhideHomeFromHdpEvent = (payload: AnalyticsPayload = {}) =>
  hotpadsEvents.emitTrackUnhideHomeFromHdpEvent(payload);

export const emitTrackReportHomeFromHdpEvent = (payload: AnalyticsPayload = {}) =>
  hotpadsEvents.emitTrackReportHomeFromHdpEvent(payload);

/** AppActions.sendEventToApi mirror — fire-and-forget API tracking call. */
export const sendEventToApi = (name: string, payload?: unknown) => devLog(`sendEventToApi:${name}`, payload as ApiPayload);

export const trackListingView = (listing: HotpadsClickstreamListing | null | undefined) => {
  hotpadsEvents.emitTrackViewHdpEvent({ listing });
};
